import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Channel,
  deviceUtils,
  Envs,
  EventType,
  LayoutComponentProps,
  useAppContext,
  useChannelContext,
  useEnvContext,
  useEventBusEmit,
  useMenuRouter,
  useRouter,
} from 'scbk-services';
import { DesktopFooter } from 'scbk-shared-module/layout/footer/DesktopFooter';
import { DeskTopOnlyProps } from 'scbk-shared-module/layout/types/DeskTopOnly';
import { cn, SkipNavigation } from 'scbk-ui-web';

import { useGlobalPopupContext } from 'scbk-shared-module';
import { STATUS_BAR_COLOR } from 'scbk-shared-module/constants';
import { PageHeader } from 'scbk-shared-module/layout';
import { DataAttributes } from 'scbk-shared-module/layout/types/HeaderType';

interface PageLayoutProps extends LayoutComponentProps, DeskTopOnlyProps {
  contentType?: 'full' | 'default' | 'global';
  showTitle?: boolean;
  isSubMode?: boolean;
  subHeaderType?: 'round' | 'white' | 'bg';
  darkMode?: boolean;
  enableLargeText?: boolean;
  isBackBtn?: boolean;
  isCloseBtn?: boolean;
  titleProps?: DataAttributes;
  closeProps?: DataAttributes;
  langBtn?: boolean;
}

/**
 * sub page 레이아웃
 * @param param0
 * @returns
 */
const PageLayout = ({
  title,
  children,
  subHeaderType,
  contentType = 'default',
  showTitle = true,
  darkMode = false,
  onHistoryClick,
  enableLargeText = false,
  isBackBtn = true,
  isCloseBtn = true,
  titleProps,
  closeProps,
  langBtn = false,
}: PageLayoutProps) => {
  const router = useRouter();
  const { channel } = useChannelContext();
  const { getEnv } = useEnvContext();
  const emit = useEventBusEmit();
  const { seniorMode, osInfo, getContext } = useAppContext();

  const isDesktop = deviceUtils.isPC();

  const isLargeText = useMemo(() => {
    return seniorMode === 'Y';
  }, [seniorMode]);

  const applied = useMemo(() => {
    return enableLargeText && isLargeText && !isDesktop; // 260803 추가
  }, [isDesktop, isLargeText, enableLargeText]);

  const pageRef = useRef<HTMLDivElement>(null);

  const [isScrolled, setIsScrolled] = useState(false);

  const [topBarOpacity, setTopBarOpacity] = useState<number>(0);

  const { goMenu } = useMenuRouter();

  const { openUserInfoPopup } = useGlobalPopupContext();

  const handleOnHistoryClick = useCallback(async () => {
    console.log('# PageLayout handleOnHistoryClick channel=', channel);
    if (channel !== Channel.MB) {
      return;
    }

    if (onHistoryClick) {
      onHistoryClick();
      return;
    }
    //TODO 뒤로가기 이벤트 처리를 해서 전역으로 관리하는것이 좋을듯
    router.skipBack();
  }, [channel, router, onHistoryClick]);
  /**
   * 닫기버튼 제어
   */
  const handleOnClose = useCallback(async () => {
    console.log('# PageLayout handleOnClose channel=', channel);
    if (channel !== Channel.MB) {
      return;
    }

    router.firstBack();
  }, [channel, router]);

  // 내알림
  const onAlertPanel = useCallback(() => {
    alert('내알림 개발 완료 후 적용 예정');
  }, []);

  // 전체 검색
  const onSearchPanel = useCallback(() => {
    goMenu('KLDSCH000');
  }, []);

  //
  useEffect(() => {
    //
    if (channel !== Channel.MB) {
      return;
    }

    const theme = STATUS_BAR_COLOR[darkMode ? 'dark' : 'light'][subHeaderType ?? 'round'];
    const color = isScrolled ? (theme.scrolled ?? '0') : (theme.default ?? '0');

    //0:black	1:white
    emit(EventType.ChangeStatusBarColor, color);
  }, [channel, isScrolled, emit, subHeaderType]);

  useEffect(() => {
    const handleScroll = () => {
      let scrolled;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      let opacity = scrollTop / 50;
      if (opacity > 1) {
        opacity = 1;
      }
      setTopBarOpacity(opacity);

      //스크롤 위치 확인
      if (isDesktop) {
        scrolled = scrollTop > 44;
      } else {
        scrolled = scrollTop > 64;
      }

      if (subHeaderType === 'round') {
        scrolled = scrollTop > 6;
      }

      setIsScrolled(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    //초기 체크
    handleScroll();

    //리사이즈 감지
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  //260609 추가
  useEffect(() => {
    if (isDesktop) return;

    const updateVh = () => {
      const vh = (window.visualViewport?.height ?? window.innerHeight) * 0.01;
      console.log(vh);
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    updateVh();

    const viewport = window.visualViewport;
    if (viewport) {
      viewport.addEventListener('resize', updateVh);
      return () => window.removeEventListener('resize', updateVh);
    } else {
      window.addEventListener('resize', updateVh);
      return () => window.removeEventListener('resize', updateVh);
    }
  }, []);

  return (
    <>
      {isDesktop && <SkipNavigation />}
      <div
        className={cn('wrap', contentType === 'global' ? 'global-wrap' : '')}
        data-ui-name="page-layout"
        ref={pageRef}
        data-text-display={String(applied)} //260803 수정
      >
        <PageHeader
          title={title}
          onHistoryClick={() => {
            handleOnHistoryClick();
          }}
          onClose={() => {
            //TODO 닫기버튼 클릭 이벤트 처리해서 전역으로 관리하는것으 좋을듯
            handleOnClose();
          }}
          subHeaderType={subHeaderType}
          onAlertPanel={onAlertPanel} //내알림
          onSearchPanel={onSearchPanel} // 검색
          isScrolled={isScrolled}
          opacity={topBarOpacity}
          isBackBtn={isBackBtn}
          isCloseBtn={isCloseBtn}
          titleProps={titleProps} //adobe 관련
          closeProps={closeProps} //adobe 관련
        />
        <main
          className={cn('default-page', contentType, subHeaderType, isScrolled && 'scrolled')}
          id="content"
        >
          {contentType === 'full' && (
            <div className={cn('full-wrap', isDesktop && 'bg-wrapper')}>
              <div className="page-container mx-auto">{children}</div>
            </div>
          )}
          {contentType === 'default' && (
            <div className="page-container mx-auto">
              {isDesktop && (
                <div className={cn('w-[600px] mx-auto')}>
                  <h1
                    className={cn(
                      isDesktop ? 'px-0' : 'px-4',
                      'text-basic-01 heading-h2-5',
                      isDesktop && showTitle ? 'block' : 'hidden',
                    )}
                    onClick={(e) => {
                      e.preventDefault();
                      openUserInfoPopup?.();
                    }}
                  >
                    {title}
                  </h1>
                </div>
              )}
              {children}
            </div>
          )}
          {contentType === 'global' && (
            <section className="page-container">
              {isDesktop && (
                <div className="w-[600px] mx-auto">
                  <h1
                    className={cn(
                      isDesktop ? 'px-0' : 'px-4',
                      'text-basic-01 heading-h2-5',
                      isDesktop && showTitle ? 'block' : 'hidden',
                    )}
                    onClick={(e) => {
                      e.preventDefault();
                      openUserInfoPopup?.();
                    }}
                  >
                    {title}
                  </h1>
                </div>
              )}
              {children}
            </section>
          )}
        </main>
        {!isDesktop && getEnv() !== Envs.PROD && (
          <div className="floating-version">{`${osInfo?.osType ?? ''} : ${getContext().app?.appVersion}`}</div>
        )}
        {isDesktop && <DesktopFooter langBtn={langBtn} />}
      </div>
    </>
  );
};

export { PageLayout };
